import React from "react"

import { useTranslation } from "react-i18next"

import { AdminPlayerBonus2 } from "services/bonus/type"
import { GameInterface } from "services/game/type"

import { formatDateTime,formatFiatCurrency } from "util/formatter"

interface FreeSpinsSectionProps {
  playerBonus: AdminPlayerBonus2
  gamesCache: Record<string, GameInterface>
}

export const FreeSpinsSection: React.FC<FreeSpinsSectionProps> = ({ playerBonus: pb, gamesCache }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  if (!pb.free_spins_awarded && !(pb.bonus.free_spins_count && pb.bonus.free_spins_count > 0)) {
    return null
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🎰</span>
        <h5 className="text-white font-semibold">{t("bonus2.players.freeSpinsInformation")}</h5>
      </div>

      {pb.free_spins_awarded > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="bg-[#0C0C1A] rounded p-3">
              <div className="text-gray-400 mb-1 text-xs">{t("bonus2.players.awarded")}</div>
              <div className="text-white font-bold text-lg">{pb.free_spins_awarded}</div>
              <div className="text-gray-500 text-xs">{t("bonus2.players.totalSpins")}</div>
            </div>

            <div className="bg-[#0C0C1A] rounded p-3">
              <div className="text-gray-400 mb-1 text-xs">{t("bonus2.players.used")}</div>
              <div className="text-orange-400 font-bold text-lg">{pb.free_spins_used}</div>
              <div className="text-gray-500 text-xs">{t("bonus2.players.spinsPlayed")}</div>
            </div>

            <div className="bg-[#0C0C1A] rounded p-3">
              <div className="text-gray-400 mb-1 text-xs">{t("bonus2.players.remaining")}</div>
              <div className="text-emerald-400 font-bold text-lg">{pb.free_spins_remaining}</div>
              <div className="text-gray-500 text-xs">{pb.free_spins_remaining === 0 ? t("bonus2.players.allUsed") : t("bonus2.players.available")}</div>
            </div>

            <div className="bg-[#0C0C1A] rounded p-3">
              <div className="text-gray-400 mb-1 text-xs">{t("bonus2.players.winnings")}</div>
              <div className="text-purple-400 font-bold text-lg">{formatFiatCurrency(Number(pb.free_spins_winnings))}</div>
              <div className="text-gray-500 text-xs">{t("bonus2.players.totalWon")}</div>
            </div>
          </div>

          {pb.free_spins_expires_at && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-gray-400">⏰ Spins expire:</span>
              <span className="text-yellow-400 font-semibold">{formatDateTime(pb.free_spins_expires_at)}</span>
              {new Date(pb.free_spins_expires_at) < new Date() && <span className="text-rose-400 text-xs">(Expired)</span>}
            </div>
          )}

          {/* Progress bar for free spins usage */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{t("bonus2.players.usageProgress")}</span>
              <span>
                {pb.free_spins_used} / {pb.free_spins_awarded} (
                {pb.free_spins_awarded > 0 ? ((pb.free_spins_used / pb.free_spins_awarded) * 100).toFixed(0) : 0}%)
              </span>
            </div>
            <div className="bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-emerald-500 to-purple-500 h-2 rounded-full transition-all"
                style={{
                  width: `${pb.free_spins_awarded > 0 ? (pb.free_spins_used / pb.free_spins_awarded) * 100 : 0}%`
                }}
              />
            </div>
          </div>

          {/* Eligible Games Section */}
          {pb.bonus.free_spins_game_ids && pb.bonus.free_spins_game_ids.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎮</span>
                <h6 className="text-white font-semibold">{t("bonus2.players.eligibleGames2")}</h6>
              </div>
              <div className="space-y-3">
                {pb.bonus.free_spins_game_ids.map((gameId, index) => {
                  const game = gamesCache[gameId]
                  return (
                    <div key={index} className="bg-[#0C0C1A] rounded-lg p-3 border border-gray-700">
                      {game ? (
                        <div className="flex gap-3">
                          {/* Game Image */}
                          <div className="flex-shrink-0">
                            <img
                              src={game.image_vertical || game.image}
                              alt={game.title || game.name}
                              className="w-20 h-20 rounded object-cover"
                              onError={(e) => {
                                e.currentTarget.src = "https://via.placeholder.com/80x80?text=No+Image"
                              }}
                            />
                          </div>

                          {/* Game Details */}
                          <div className="flex-1 min-w-0">
                            <h6 className="text-white font-semibold text-sm mb-1 truncate">{game.title || game.name}</h6>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-400">{t("bonus2.players.provider")} </span>
                                <span className="text-white">{game.gameProvider?.title || game.provider}</span>
                              </div>

                              <div>
                                <span className="text-gray-400">{t("bonus2.players.producer")} </span>
                                <span className="text-purple-400">{game.producer}</span>
                              </div>

                              <div>
                                <span className="text-gray-400">{t("bonus2.players.category")} </span>
                                <span className="text-white">{game.category}</span>
                              </div>

                              <div>
                                <span className="text-gray-400">ID: </span>
                                <span className="text-gray-300 font-mono">{gameId}</span>
                              </div>
                            </div>

                            {game.has_freespins && (
                              <div className="mt-2">
                                <span className="inline-block px-2 py-0.5 bg-emerald-900/30 border border-emerald-600/40 text-emerald-300 text-xs rounded">
                                  ✓ Free Spins Supported
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 bg-rose-900/20 border border-rose-600/40 rounded">
                          <span className="text-rose-400">⚠️</span>
                          <div className="flex-1">
                            <div className="text-rose-300 font-semibold text-sm">{t("bonus2.players.gameNotFound")}</div>
                            <div className="text-gray-400 text-xs">
                              Game ID &quot;{gameId}&quot; is not available in the current game catalog
                            </div>
                          </div>
                          <span className="text-white font-mono text-sm bg-[#1A1A2E] px-2 py-1 rounded">{gameId}</span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {pb.bonus.free_spins_game_ids.length === 1 && (
                <div className="mt-3 text-xs text-gray-400">💡 Free spins are only available on this specific game</div>
              )}
            </div>
          )}
        </>
      ) : (
        pb.bonus.free_spins_count &&
        pb.bonus.free_spins_count > 0 && (
          <>
            <div className="bg-orange-900/20 border border-orange-600/40 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-orange-400 text-lg">⚠️</span>
                <div>
                  <div className="text-orange-300 font-semibold text-sm">{t("bonus2.players.notActivatedYet")}</div>
                  <div className="text-gray-400 text-sm">
                    {pb.bonus.free_spins_count} free spins will be awarded when bonus is activated
                  </div>
                </div>
              </div>
            </div>

            {/* Eligible Games Section - Show even when not activated */}
            {pb.bonus.free_spins_game_ids && pb.bonus.free_spins_game_ids.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🎮</span>
                  <h6 className="text-white font-semibold">{t("bonus2.players.eligibleGamesAvailableAfter")}</h6>
                </div>
                <div className="space-y-3">
                  {pb.bonus.free_spins_game_ids.map((gameId, index) => {
                    const game = gamesCache[gameId]
                    return (
                      <div key={index} className="bg-[#0C0C1A] rounded-lg p-3 border border-gray-700 opacity-75">
                        {game ? (
                          <div className="flex gap-3">
                            {/* Game Image */}
                            <div className="flex-shrink-0 relative">
                              <img
                                src={game.image_vertical || game.image}
                                alt={game.title || game.name}
                                className="w-20 h-20 rounded object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "https://via.placeholder.com/80x80?text=No+Image"
                                }}
                              />
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded">
                                <span className="text-white text-xs font-semibold">🔒 Locked</span>
                              </div>
                            </div>

                            {/* Game Details */}
                            <div className="flex-1 min-w-0">
                              <h6 className="text-white font-semibold text-sm mb-1 truncate">{game.title || game.name}</h6>

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-400">{t("bonus2.players.provider")} </span>
                                  <span className="text-white">{game.gameProvider?.title || game.provider}</span>
                                </div>

                                <div>
                                  <span className="text-gray-400">{t("bonus2.players.producer")} </span>
                                  <span className="text-purple-400">{game.producer}</span>
                                </div>

                                <div>
                                  <span className="text-gray-400">{t("bonus2.players.category")} </span>
                                  <span className="text-white">{game.category}</span>
                                </div>

                                <div>
                                  <span className="text-gray-400">ID: </span>
                                  <span className="text-gray-300 font-mono">{gameId}</span>
                                </div>
                              </div>

                              {game.has_freespins && (
                                <div className="mt-2">
                                  <span className="inline-block px-2 py-0.5 bg-emerald-900/30 border border-emerald-600/40 text-emerald-300 text-xs rounded">
                                    ✓ Free Spins Supported
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 p-3 bg-rose-900/20 border border-rose-600/40 rounded">
                            <span className="text-rose-400">⚠️</span>
                            <div className="flex-1">
                              <div className="text-rose-300 font-semibold text-sm">{t("bonus2.players.gameNotFound")}</div>
                              <div className="text-gray-400 text-xs">
                                Game ID &quot;{gameId}&quot; is not available in the current game catalog
                              </div>
                            </div>
                            <span className="text-white font-mono text-sm bg-[#1A1A2E] px-2 py-1 rounded">{gameId}</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {pb.bonus.free_spins_game_ids.length === 1 && (
                  <div className="mt-3 text-xs text-gray-400">💡 Free spins will only be available on this specific game</div>
                )}
              </div>
            )}
          </>
        )
      )}
    </div>
  )
}
